import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { isEqual } from 'lodash';

import { api } from '../../../services/api';
import { dateService } from '../../../services/date';
import { session } from '../../../services/session';

import StrategySelection from './strategy-selection';

import { formatValue } from '../../base/common/utils';
import { setSnackbar } from '../../../redux/actions';

import './default-strategy.scss';

const dateFrom = dateService.getCurrentDateTime().format('YYYY-MM');
const dateTo = dateService
  .getCurrentDateTime()
  .add(1, 'month')
  .format('YYYY-MM');

const DefaultStrategy = ({ regexes, maxStrategyValue, maxBudget, snackbarMsgs }) => {
  const merchantStrategiesContainer = React.createRef();

  const infoEndpoint = `/api/v0/merchants/${session.orgId}/`;
  const budgetEndpoint = `/api/v0/merchants/${session.orgId}/budgets/`;

  // Redux
  const dispatch = useDispatch();

  // Strategy
  const [defaultStrategy, setDefaultStrategy] = useState({
    manualClickValue: '',
    pricingModel: '',
    roiGoal: '',
    strategyType: '',
  });
  const [strategy, setStrategy] = useState({ ...defaultStrategy });

  // Budget
  const [defaultBudget, setDefaultBudget] = useState({
    budgetId: '',
    budgetAmount: '',
    budgetAmountInput: '',
    budgetSpent: '',
  });
  const [budget, setBudget] = useState({ ...defaultBudget });

  // Errors
  const [errors, setErrors] = useState({
    roi: false,
    cpc: false,
    budgetAmount: false,
    addStrategy: false,
  });

  // UI
  const [changesMade, setChangesMade] = useState(false);
  const [loading, setLoading] = useState(true);

  const budgetParams = { date_from: dateFrom, date_to: dateTo };
  const outOfBudget =
    parseFloat(budget.budgetAmount) > 0 &&
    parseFloat(budget.budgetSpent) >= parseFloat(budget.budgetAmount);
  const budgetError = outOfBudget || errors.budgetAmount;

  useEffect(() => {
    getMerchantDetails();
  }, []);

  useEffect(() => {
    setStrategy(strategy => {
      return { ...strategy, ...defaultStrategy };
    });
  }, [defaultStrategy]);

  useEffect(() => {
    setBudget(budget => {
      return { ...budget, ...defaultBudget };
    });
  }, [defaultBudget]);

  async function getMerchantDetails() {
    const infoResp = await api.get(infoEndpoint);
    await setStrategyInfo(infoResp.data.data[0]);

    const budgetResp = await api.get(budgetEndpoint, budgetParams);
    await setBudgetInfo(budgetResp.data.data[0]);

    setLoading(false);
  }

  function setStrategyInfo(resp) {
    const isFlatRateMerchant = resp.pricing_model === 'EPP' && resp.manual_click_value !== null;
    const flatOrCpc = isFlatRateMerchant || resp.pricing_model === 'CPC';

    setDefaultStrategy({
      ...defaultStrategy,
      manualClickValue: formatValue(resp.manual_click_value, 'currency') || '',
      roiGoal: resp.roi_goal,
      pricingModel: resp.pricing_model,
      strategyType: flatOrCpc ? 'cpc' : 'roi',
    });
  }

  function setBudgetInfo(resp) {
    const currentMonth = (resp.budgets ? resp.budgets[dateFrom] : resp) || {};
    const currentBudget = currentMonth.budget_amount;

    setDefaultBudget({
      ...defaultBudget,
      budgetId: currentMonth.budget_id || null,
      budgetAmount: currentBudget || '',
      budgetAmountInput: currentBudget ? formatValue(currentBudget, 'currency') : '',
      budgetSpent: parseFloat(currentMonth.spent_amount || '').toFixed(2),
    });
  }

  // Strategy Functions
  function selectStrategyType(type) {
    setChangesMade(true);
    setStrategy({
      ...strategy,
      strategyType: type,
    });
    setErrors({
      ...errors,
      [type === 'roi' ? 'cpc' : 'roi']: false,
    });
  }

  function updateStrategyValue(e) {
    setChangesMade(true);
    setStrategy({
      ...strategy,
      [strategy.strategyType === 'cpc' ? 'manualClickValue' : 'roiGoal']: e.target.value,
    });
  }

  function validateStrategyInput(input) {
    const inputValue = input || '';
    const parsedInput = parseFloat(inputValue.replace(/\$|,/g, '')); // String currency formatting [$,]
    const isValidInput =
      strategy.strategyType === 'cpc'
        ? parsedInput <= maxStrategyValue && inputValue.match(regexes.currency)
        : parsedInput <= maxStrategyValue && inputValue.match(regexes.roi);
    const strategyValueInput =
      strategy.strategyType === 'cpc' ? formatValue(parsedInput, 'currency') : input;

    setStrategy({
      ...strategy,
      ...(isValidInput && {
        [strategy.strategyType === 'cpc' ? 'manualClickValue' : 'roiGoal']: strategyValueInput,
      }),
    });

    setErrors({
      ...errors,
      [strategy.strategyType]: !isValidInput,
    });
  }

  // Budget Functions
  function updateBudgetValue(e) {
    setChangesMade(true);
    setBudget({
      ...budget,
      budgetAmountInput: e.target.value,
    });
  }

  function validateBudgetInput() {
    const inputValue = budget.budgetAmountInput || '';
    const parsedInput = parseFloat(inputValue.replace(/\$|,/g, ''));
    const budgetAmount = parsedInput.toFixed(2);
    const isValidBudget = parsedInput <= maxBudget && inputValue.match(regexes.currency);

    setBudget({
      ...budget,
      ...(isValidBudget && {
        budgetAmount,
        budgetAmountInput: formatValue(budgetAmount, 'currency'),
      }),
    });

    setErrors({
      ...errors,
      budgetAmount: !isValidBudget,
    });

    return isValidBudget;
  }

  // UI Functions
  function triggerSnackbar(key) {
    if (key) return dispatch(setSnackbar(snackbarMsgs[key]));

    Object.keys(errors).forEach(errorKey => {
      if (errors[errorKey]) return dispatch(setSnackbar(snackbarMsgs[errorKey]));
    });
  }

  function hasNoError() {
    // TODO: only Bandaid fix for onBlur race condition with onClick form submit, refactor
    const isBudgetInputValid = validateBudgetInput(budget.budgetAmountInput);
    if (!isBudgetInputValid) return false;
    return Object.keys(errors).every(i => !errors[i]);
  }

  function cancelChanges() {
    setStrategy({ ...defaultStrategy });
    setBudget({ ...defaultBudget });
    setErrors({ roi: false, cpc: false, budgetAmount: false, addStrategy: false });
    setChangesMade(false);
  }

  async function saveChanges() {
    if (!session.isAdmin) {
      // eslint-disable no-alert
      alert('Ability to save is currently disabled.');
      return;
    }

    if (!hasNoError()) return triggerSnackbar();

    // Update Strategy Type and Value
    if (
      !isEqual(defaultStrategy.roiGoal, strategy.roiGoal) ||
      !isEqual(defaultStrategy.manualClickValue, strategy.manualClickValue) ||
      !isEqual(defaultStrategy.strategyType, strategy.strategyType)
    ) {
      const input =
        strategy.strategyType === 'roi'
          ? strategy.roiGoal && parseFloat(strategy.roiGoal)
          : strategy.manualClickValue &&
            parseFloat(strategy.manualClickValue.replace(/\$|,/g, '')).toFixed(2);

      const params =
        strategy.pricingModel === 'EPP' && strategy.strategyType === 'roi'
          ? { roi_goal: input, manual_click_value: null }
          : { manual_click_value: input };

      const resp = await api.put(infoEndpoint, params);
      setStrategyInfo(resp.data.data[0]);
    }

    // Update Budget Amount
    if (!isEqual(defaultBudget.budgetAmount, budget.budgetAmount)) {
      const params = { budget_amount: budget.budgetAmount, year_month: dateFrom };

      const resp = budget.budgetId
        ? await api.put(`${budgetEndpoint}${budget.budgetId}/`, params)
        : await api.post(`${budgetEndpoint}`, params);

      setBudgetInfo(resp.data.data[0]);
    }

    triggerSnackbar('saved');
  }

  const budgetErrorMsg = budgetError ? (outOfBudget ? 'Out of budget' : 'Invalid budget') : null;

  const updateActions = changesMade ? (
    <>
      <hr />
      <div className="actions">
        <button className="cancel-btn" onClick={() => cancelChanges()}>
          Cancel
        </button>
        <button className="save-btn" onClick={() => saveChanges()}>
          Save
        </button>
      </div>
    </>
  ) : null;

  return !loading ? (
    <div
      ref={merchantStrategiesContainer}
      tabIndex="0"
      className={`default-strategy-container${changesMade ? ' changes' : ''}`}
      onFocus={() => merchantStrategiesContainer.current.classList.toggle('selected')}
      onBlur={() => merchantStrategiesContainer.current.classList.toggle('selected')}
    >
      <div className="default-strategy">
        <StrategySelection
          strategyType={strategy.strategyType}
          strategyValueInput={
            strategy.strategyType === 'roi' ? strategy.roiGoal : strategy.manualClickValue
          }
          selectStrategyType={selectStrategyType}
          updateStrategyValue={updateStrategyValue}
          validateStrategyInput={validateStrategyInput}
          pricingModel={strategy.pricingModel}
          paymentToggleVisible={false}
          hasError={errors[strategy.strategyType]}
        />
      </div>
      <div className="default-budget">
        <div className="budget-label">Monthly Budget</div>
        <input
          className={`budget-value-input${budgetError ? ' error' : ''}`}
          value={budget.budgetAmountInput}
          placeholder="Set Budget"
          maxLength="20"
          onChange={updateBudgetValue}
          onBlur={validateBudgetInput}
        />
        <div className={`budget-spent${budgetError ? ' error' : ''}`}>
          {formatValue(budget.budgetSpent, 'currency')} spent
        </div>
        <div className="error-label">{budgetErrorMsg}</div>
      </div>
      {updateActions}
    </div>
  ) : null;
};

export default DefaultStrategy;
